import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../CustomButton';
import InputField from '../ui/InputField';
import { useLazyDownloadAuditExportQuery } from '../../api/invoice/invoiceApiSlice';

interface Props {
    /** The tenant's ADR-0012 mode. Only an issuing tenant has invoices to export. */
    invoiceIssuer: string | null | undefined;
}

/** First and last day of the previous calendar year — the usual audit request. */
const lastYear = (): { from: string, to: string } => {
    const year = new Date().getFullYear() - 1;
    return { from: `${year}-01-01`, to: `${year}-12-31` };
};

/**
 * The GoBD audit export (#1644, WP5): invoices, their lines and VAT
 * breakdown, the money ledger and a completeness check of every number range,
 * with the data description auditors' tools read (Datenträgerüberlassung Z3).
 */
const AuditExportSection = ({ invoiceIssuer }: Readonly<Props>) => {
    const { t } = useTranslation();
    const [{ from, to }, setPeriod] = useState(lastYear);
    const [failure, setFailure] = useState<string | null>(null);
    const [download, { isFetching }] = useLazyDownloadAuditExportQuery();

    if (invoiceIssuer !== 'kolula') return null;

    const onDownload = async () => {
        setFailure(null);
        try {
            const blob = await download({ from, to }).unwrap();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `kolula-audit-export_${from}_${to}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            // The body is a Blob here, so the server's JSON message is not
            // readable without parsing it; the causes the user can act on are
            // the role and the dates, and the hint says both.
            setFailure(t('account.audit_export_failed'));
        }
    };

    return (
        <div className={'bg-text-contrast rounded-[5px] shadow-sm p-5'}>
            <h2 className={'text-base font-semibold text-foreground'}>{t('account.audit_export_section')}</h2>
            <p className={'text-sm text-foreground/60 mt-1 mb-4'}>{t('account.audit_export_desc')}</p>
            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                <InputField
                    labelAbove
                    type={'date'}
                    label={t('account.audit_export_from')}
                    value={from}
                    onChange={(value: string) => setPeriod((p) => ({ ...p, from: value }))}
                />
                <InputField
                    labelAbove
                    type={'date'}
                    label={t('account.audit_export_to')}
                    value={to}
                    onChange={(value: string) => setPeriod((p) => ({ ...p, to: value }))}
                />
            </div>
            <div className={'mt-4'}>
                <CustomButton
                    title={t('account.audit_export_download')}
                    loading={isFetching}
                    disabled={isFetching || !from || !to || from > to}
                    onClick={onDownload}
                />
            </div>
            {failure ? <p className={'mt-3 text-sm text-foreground/80'}>{failure}</p> : null}
        </div>
    );
};

export default AuditExportSection;
